import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { MenuProductSkeleton } from 'features/menu/components/menu-product-skeleton';
import { ProductDetail } from 'features/menu/components/product-detail';
import { ProductDetailCardModal } from 'features/menu/components/product-detail/product-detail-card-modal';
import { ProductWizardProvider } from 'features/menu/components/product-wizard';
import { useProductWizardReward } from 'features/menu/components/product-wizard/use-product-wizard-reward';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { useLogMenuContent } from 'features/menu/pages/menu-object-page/use-log-menu-content';
import { useStoreContext } from 'features/store';

import { MenuDayPartUnavailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-day-part-unavailable';
import { asPreviewMenuEntry } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/as-preview-menu-entry';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { findMainMenuNodeById } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/find-main-menu-node-by-id';
import {
  findParentSectionId,
  findTopLevelSectionId,
} from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/find-top-level-section-id';
import { isEntryOutOfActiveDayParts } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/is-entry-out-of-active-day-parts';
import { navigateToUnavailableFullMenu } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/navigate-to-unavailable-full-menu';
import { navigateToUnavailableMenuSection } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/navigate-to-unavailable-menu-section';
import { resolveUnavailableProductRecovery } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/resolve-unavailable-product-recovery';
import {
  useDeeplinksFlowExperiment,
  useExposeDeeplinksFlowExperiment,
} from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useLocale } from 'state/intl';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isProductRouteMenuEntry } from 'state/unified-menu/utils';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

// new - one menu
export const MenuProductPage = () => {
  useMenuScroll();
  const router = useRouter();
  const { language } = useLocale();
  const { store } = useStoreContext();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();
  const isMinimalCartUIEnabled = useMinimalCartUi();
  const {
    indexedMenu,
    isLoading: isMenuLoading,
    rawMenu,
    mainMenu,
    loadedStoreNumber,
    hasError: hasMenuError,
    activeDayParts,
    isDayPartsSourceResolved,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const dayPartIds = isDayPartsSourceResolved
    ? activeDayParts.map(dayPart => dayPart.key)
    : undefined;
  const rewardState = useProductWizardReward();
  const isLoading = isMenuLoading || rewardState.loading;

  const { pathname, params } = useRoute<{
    id: string;
    cartIdEditing: string;
    menuObjectType?: string;
  }>();
  const { id = '', cartIdEditing = '', menuObjectType: routeMenuObjectType = '' } = params || {};

  const { cartEntries: legacyCartEntries } = useCart();
  const { cartEntries } = useMinimalCart();

  const menuObject = indexedMenu.find(id);
  const menuObjectType = String(menuObject?.type ?? routeMenuObjectType);

  useLogMenuContent({
    loggedValues: {
      id,
      name: menuObject?.name?.locale ?? '',
      menuType: menuObjectType,
    },
    pathname,
  });

  const handleChooseAnotherRestaurant = useCallback(() => {
    const typeSegment = menuObjectType ? menuObjectType.toLowerCase() : 'item';
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${id}`,
        unavailableItemId: id,
      },
    });
  }, [id, menuObjectType, router]);

  // TODO: implement isOffer
  const isOffer = false;
  const isProductRouteEntry = Boolean(
    menuObject && isProductRouteMenuEntry(menuObject, posServiceMode)
  );
  // Do not treat "menu still loading / not yet for this store" as unavailable —
  // that raced and redirected available items to the section list.
  const storeNumber = store?.number ?? null;
  const isStoreMenuReady =
    !isMenuLoading && !!storeNumber && loadedStoreNumber === storeNumber && indexedMenu.isLoaded();

  // Daypart is deliberately not a rejection term here. `mainMenu` is itself
  // daypart-filtered, so testing membership against it was circular: an entry
  // dropped for its daypart could never satisfy the check meant to rescue it,
  // and ordinary in-app navigation started resolving to "unavailable". The
  // store's own menu is the unfiltered source of truth — if `indexedMenu`
  // carries the product, it stays orderable. Daypart copy still covers
  // deeplinks to entries the store menu omits, via `catalogEntryForDayPart`.
  const isUnavailable = isStoreMenuReady && (!menuObject || !isProductRouteEntry);

  useExposeDeeplinksFlowExperiment(isUnavailable && !isExperimentLoading);

  // Catalog resolves parent section when the store menu omits the item entirely.
  // Also used for daypart copy when the store index omits the entry.
  const {
    entries: catalogEntries,
    loading: catalogLoading,
    error: catalogError,
    rootMenuEntryId: catalogRootId,
  } = useOneMenuCatalog({
    skip: !isUnavailable,
  });

  const catalogEntryForDayPart = useMemo(() => {
    if (menuObject || !id || catalogLoading) {
      return undefined;
    }
    const entry = catalogEntries.find(catalogEntry => catalogEntry.id === id);
    return entry ? asPreviewMenuEntry(entry, language) : undefined;
  }, [catalogEntries, catalogLoading, id, language, menuObject]);

  const dayPartSource = menuObject ?? catalogEntryForDayPart;
  const isOutOfDayPart = isEntryOutOfActiveDayParts(dayPartSource, dayPartIds);

  const [didAttemptSectionRedirect, setDidAttemptSectionRedirect] = useState(false);
  const didNavigateRef = useRef(false);

  const sectionIdForUnavailable = useMemo(() => {
    if (!isUnavailable || !id) {
      return undefined;
    }

    const storeEntries = rawMenu?.entries ?? [];
    const fromStore =
      findParentSectionId(storeEntries, id) ??
      findTopLevelSectionId(storeEntries, id, rawMenu?.rootMenuEntryId);
    if (fromStore) {
      return fromStore;
    }

    if (catalogLoading) {
      return undefined;
    }

    if (catalogEntries.length) {
      const localized = catalogEntries.map(entry => asPreviewMenuEntry(entry, language));
      return (
        findParentSectionId(localized, id) ?? findTopLevelSectionId(localized, id, catalogRootId)
      );
    }

    const walk = (nodes: typeof mainMenu): string | undefined => {
      for (const node of nodes) {
        if (node.children.some(child => child.id === id)) {
          return node.id;
        }
        const nested = walk(node.children);
        if (nested) {
          return nested;
        }
      }
      return undefined;
    };
    return walk(mainMenu ?? []);
  }, [
    catalogEntries,
    catalogLoading,
    catalogRootId,
    id,
    isUnavailable,
    language,
    mainMenu,
    rawMenu,
  ]);

  const catalogSettled = !catalogLoading || !!catalogError || catalogEntries.length > 0;

  useEffect(() => {
    if (!isDeeplinksFlowEnabled || !isUnavailable || !id || didNavigateRef.current) {
      return;
    }

    // Daypart mismatches stay on this PDP with daypart copy — do not open section /
    // Full Menu recovery chrome.
    if (isOutOfDayPart) {
      setDidAttemptSectionRedirect(true);
      return;
    }

    if (!sectionIdForUnavailable && !catalogSettled) {
      return;
    }

    if (sectionIdForUnavailable) {
      didNavigateRef.current = true;
      const recovery = resolveUnavailableProductRecovery({
        itemId: id,
        sectionId: sectionIdForUnavailable,
        sectionExistsAtStore: Boolean(
          findMainMenuNodeById(mainMenu ?? [], sectionIdForUnavailable)
        ),
        hasOtherMenuSections: (mainMenu ?? []).length > 0,
      });
      if (recovery.kind === 'section') {
        navigateToUnavailableMenuSection(router, recovery.sectionId, id);
      } else if (recovery.kind === 'fullMenu') {
        navigateToUnavailableFullMenu(router, {
          itemId: recovery.itemId,
          ...(recovery.sectionId ? { sectionId: recovery.sectionId } : {}),
        });
      } else {
        setDidAttemptSectionRedirect(true);
      }
      return;
    }

    // No parent section — still prefer Full Menu product chrome when possible.
    const noSectionRecovery = resolveUnavailableProductRecovery({
      itemId: id,
      sectionId: undefined,
      sectionExistsAtStore: false,
      hasOtherMenuSections: (mainMenu ?? []).length > 0,
    });
    if (noSectionRecovery.kind === 'fullMenu') {
      didNavigateRef.current = true;
      navigateToUnavailableFullMenu(router, { itemId: noSectionRecovery.itemId });
      return;
    }

    setDidAttemptSectionRedirect(true);
  }, [
    catalogSettled,
    id,
    isDeeplinksFlowEnabled,
    isOutOfDayPart,
    isUnavailable,
    mainMenu,
    router,
    sectionIdForUnavailable,
  ]);

  // Reset navigation guard when the target item or store changes.
  useEffect(() => {
    didNavigateRef.current = false;
    setDidAttemptSectionRedirect(false);
  }, [id, storeNumber]);

  if (menuObject && !isProductRouteEntry && isStoreMenuReady) {
    logger.warn({
      message: '[MenuProductPage] blocked non-standalone One Menu product route',
      context: {
        id,
        name: menuObject.name?.locale,
        type: menuObject.type,
        posServiceMode,
      },
    });
  }

  // Still fetching this store's menu — never redirect yet.
  if (!isStoreMenuReady && !hasMenuError) {
    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuProductSkeleton type={menuObjectType} />
      </ProductDetailCardModal>
    );
  }

  // Errored One Menu load: never fall through to ProductWizard with a missing
  // or previous-store menuObject.
  if (hasMenuError || !isStoreMenuReady) {
    if (isExperimentLoading) {
      return (
        <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
          <MenuProductSkeleton type={menuObjectType} />
        </ProductDetailCardModal>
      );
    }

    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuItemUnavailable
          isOffer={isOffer}
          onChooseAnotherRestaurant={
            isDeeplinksFlowEnabled ? handleChooseAnotherRestaurant : undefined
          }
        />
      </ProductDetailCardModal>
    );
  }

  if (isUnavailable) {
    // Wait for Amplitude before rendering either arm — otherwise treatment users
    // briefly get the control unavailable modal (no recovery CTA) and can act on it.
    // Treatment: keep skeleton until section redirect (or fall through to Tap-here).
    // Control: show the legacy unavailable modal once assignment is known.
    if (
      isExperimentLoading ||
      (isDeeplinksFlowEnabled &&
        !isOutOfDayPart &&
        (!didAttemptSectionRedirect || sectionIdForUnavailable))
    ) {
      return (
        <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
          <MenuProductSkeleton type={menuObjectType} />
        </ProductDetailCardModal>
      );
    }

    if (isDeeplinksFlowEnabled && isOutOfDayPart && (dayPartSource?.dayParts?.length ?? 0) > 0) {
      return (
        <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
          <MenuDayPartUnavailable kind="item" dayParts={dayPartSource!.dayParts!} />
        </ProductDetailCardModal>
      );
    }

    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuItemUnavailable
          isOffer={isOffer}
          onChooseAnotherRestaurant={
            isDeeplinksFlowEnabled ? handleChooseAnotherRestaurant : undefined
          }
        />
      </ProductDetailCardModal>
    );
  }

  if (rewardState.unavailable) {
    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuItemUnavailable isOffer={isOffer} />
      </ProductDetailCardModal>
    );
  }

  // Unready/mismatched store menu without an error flag, or any other path that
  // reached here without a product — do not mount ProductWizard on undefined.
  if (!menuObject) {
    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuProductSkeleton type={menuObjectType} />
      </ProductDetailCardModal>
    );
  }

  const editingCartEntry = isMinimalCartUIEnabled
    ? cartEntries.find(x => x.cartId === cartIdEditing)
    : legacyCartEntries.find(x => x.cartId === cartIdEditing);

  return (
    <ProductDetailCardModal
      testID={isLoading ? undefined : 'menu-product-page'}
      noDesktopWebMaxWidth
      hasInternalFooter={!isLoading}
    >
      {isLoading ? (
        <MenuProductSkeleton type={menuObjectType} />
      ) : (
        <ProductWizardProvider
          key={`${menuObject.id}:${cartIdEditing || ''}`}
          menuObject={menuObject}
          editingCartEntry={editingCartEntry}
          rewardState={rewardState}
        >
          <ProductDetail />
        </ProductWizardProvider>
      )}
    </ProductDetailCardModal>
  );
};
